import type { ReactNode, SVGProps } from 'react';

/**
 * Glyphs the mobile chrome needs that `NavIcon` doesn't carry, following its
 * conventions exactly — 24×24 viewBox, no fill, currentColor stroke, width 2,
 * round caps and joins.
 *
 * These used to sit in apps/admin because web-shared was frozen for that piece
 * of work; that reason is gone. A glyph belongs here only while it is genuinely
 * mobile-chrome-only — anything a page wants belongs in NavIcon.
 *
 * Every other glyph in the design prototype already exists in NavIcon and MUST
 * be taken from there rather than re-drawn:
 *   tab bar    → dashboard · clipboard-check · truck · alert-triangle · list
 *   app bar    → chevron-left (here) · chevron-right
 *   rows/cards → search · check-circle · x-circle · bell · log-out
 *   actions    → camera (the "Take photo" tile) · x (the photo remove button)
 */

type MobileIconName = 'chevron-left';

const PATHS: Record<MobileIconName, ReactNode> = {
  // Back affordance in the mobile app bar. NavIcon has chevron-right and
  // chevron-up but never needed the left-facing one — the desktop shell
  // navigates with the sidebar, not a back button.
  'chevron-left': <path d="m15 18-6-6 6-6" />,
};

export type { MobileIconName };

export function MobileIcon({
  name,
  size = 18,
  ...rest
}: { name: MobileIconName; size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
